import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { EstageLogo } from '@/components/landing';
import { fetchPostBySlug, type BlogPost } from '@/services/blogService';

interface BlogPostViewProps {
  slug: string;
  onNavigateList: () => void;
  onNavigateHome: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export const BlogPostView: React.FC<BlogPostViewProps> = ({ slug, onNavigateList, onNavigateHome }) => {
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchPostBySlug(slug)
      .then((data) => {
        if (!data) setError('Dieser Beitrag konnte nicht gefunden werden.');
        else setPost(data);
      })
      .catch(() => setError('Beitrag konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="landing-root blog-root">
      {/* Navbar */}
      <nav className="lp-nav">
        <div className="lp-nav-container">
          <a href="/" className="lp-nav-logo" onClick={(e) => { e.preventDefault(); onNavigateHome(); }}>
            <EstageLogo />
          </a>
          <div className="lp-nav-links">
            <a href="/blog" onClick={(e) => { e.preventDefault(); onNavigateList(); }}>← Alle Beiträge</a>
          </div>
          <div className="lp-nav-actions">
            <a href="/" className="lp-btn-nav-cta" onClick={(e) => { e.preventDefault(); onNavigateHome(); }}>
              Jetzt kostenlos testen
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z"/>
              </svg>
            </a>
          </div>
        </div>
      </nav>

      <main className="blog-post-main">
        {loading && (
          <div className="blog-loading" style={{ minHeight: '60vh' }}>
            <div className="blog-spinner" />
          </div>
        )}

        {error && (
          <div className="blog-post-error">
            <p>{error}</p>
            <button className="lp-btn-primary inline" onClick={onNavigateList}>
              Alle Beiträge ansehen
            </button>
          </div>
        )}

        {post && (
          <>
            {/* Post Header */}
            <div className="blog-post-header">
              <div className="blog-post-header-inner">
                <button className="blog-back-btn" onClick={onNavigateList}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5"/>
                  </svg>
                  Alle Beiträge
                </button>

                {post.tags.length > 0 && (
                  <div className="blog-card-tags" style={{ marginTop: 24 }}>
                    {post.tags.map(tag => (
                      <span key={tag} className="blog-tag">{tag}</span>
                    ))}
                  </div>
                )}

                <h1 className="blog-post-title">{post.title}</h1>

                <div className="blog-post-meta">
                  <span>{post.author}</span>
                  {post.published_at && <span>·</span>}
                  {post.published_at && <span>{formatDate(post.published_at)}</span>}
                  {post.reading_time_minutes && <span>·</span>}
                  {post.reading_time_minutes && <span>{post.reading_time_minutes} Min. Lesezeit</span>}
                </div>
              </div>
            </div>

            {/* Cover Image */}
            {post.cover_image_url && (
              <div className="blog-post-cover">
                <img src={post.cover_image_url} alt={post.title} />
              </div>
            )}

            {/* Markdown Content */}
            <div className="blog-post-content-wrap">
              <div className="blog-post-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                >
                  {post.content}
                </ReactMarkdown>
              </div>

              {/* CTA */}
              <div className="blog-post-cta">
                <h3>Bereit, deine Immobilienfotos zu transformieren?</h3>
                <p>Teste Estage jetzt kostenlos mit 3 Credits — keine Anmeldung nötig.</p>
                <button className="lp-btn-primary inline" onClick={onNavigateHome}>
                  Jetzt kostenlos testen
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z"/>
                  </svg>
                </button>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <div className="blog-footer">
        <div className="blog-footer-inner">
          <span>&copy; 2026 Estage.Ai</span>
          <a href="/blog" onClick={(e) => { e.preventDefault(); onNavigateList(); }}>Alle Beiträge</a>
          <a href="/" onClick={(e) => { e.preventDefault(); onNavigateHome(); }}>Zurück zur Startseite</a>
        </div>
      </div>
    </div>
  );
};
